import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, LabelList, ResponsiveContainer,
} from 'recharts';
import {
  ArrowLeft, CalendarCheck, AppWindow, Trophy, CheckCircle, CaretRight,
} from '@phosphor-icons/react';
import { ModuleShell, SkeletonBlock, EmptyState } from '../../../components/module-ui';
import {
  fetchResolutions, peopleAcrossApps, filterPeople, monthLabel, plural, pct, trend,
  Insights, MonthPicker, MonthTable, OneMonthTable, SyncedHScroll, linkBtn, th, td, tf,
} from '../../../components/dashboard/ApplicationResolutionsWidget';

const VIEWS = [
  { id: 'month', label: 'By month' },
  { id: 'application', label: 'By application' },
  { id: 'person', label: 'By person' },
];
const PERIODS = [3, 6, 12];

/**
 * Tickets solved 360° — the complete view behind the three Overview cards
 * (Tickets solved per month / by application / Who solved the tickets).
 * URL: /dashboard/tickets/pipeline?mode=solved&view=month|application|person
 *      &months=3|6|12&app=<application key>&month=<YYYY-MM>
 */
export default function Solved360Page() {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const view = VIEWS.some((v) => v.id === sp.get('view')) ? sp.get('view') : 'month';
  const months = PERIODS.includes(Number(sp.get('months'))) ? Number(sp.get('months')) : 6;
  const appKey = sp.get('app') || 'all';
  const month = sp.get('month') || 'all';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchResolutions(months)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.detail || 'Could not load solved tickets.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [months]);

  const setParams = (patch) => {
    const next = new URLSearchParams(sp);
    Object.entries(patch).forEach(([k, v]) => {
      if (v == null || v === '' || v === 'all') next.delete(k);
      else next.set(k, String(v));
    });
    next.set('mode', 'solved');
    setSp(next, { replace: true });
  };

  const app = data && appKey !== 'all' ? data.applications.find((a) => a.key === appKey) : null;

  const model = useMemo(() => {
    if (!data || !data.applications.length) return null;
    const apps = app ? [app] : data.applications;
    const people = (app ? app.people : peopleAcrossApps(data)).map((p, i) => ({ ...p, key: p.user_id, rank: i + 1 }));
    const monthTicket = (k) => (app ? app.by_month[k].tickets : data.month_totals[k].tickets);
    const monthPeople = (k) => (app ? app.by_month[k].people : data.month_totals[k].people);
    const activeMonths = data.months.filter((k) => monthTicket(k) > 0);
    const tickets = apps.reduce((s, a) => s + a.tickets_total, 0);
    const shared = apps.reduce((s, a) => s + a.multi_person_total, 0);
    const topOfMonth = (k) => [...people].sort((a, b) => (b.counts[k] || 0) - (a.counts[k] || 0))[0];
    const topAppOfMonth = (k) => [...data.applications].sort((a, b) => b.by_month[k].tickets - a.by_month[k].tickets)[0];
    return {
      apps, people, monthTicket, monthPeople, activeMonths, tickets, shared, topOfMonth, topAppOfMonth,
    };
  }, [data, app]);

  const pickedMonth = model && month !== 'all' && model.activeMonths.includes(month) ? month : 'all';

  const openPerson = (userId) => {
    const q = new URLSearchParams({ assigned_to: userId, view: 'closed' });
    if (app?.product_id) q.set('product_id', app.product_id);
    navigate(`/dashboard/tickets/pipeline?${q.toString()}`);
  };

  const insights = useMemo(() => {
    if (!model || !model.tickets) return [];
    const busiest = model.activeMonths.reduce((b, k) => (model.monthTicket(k) > model.monthTicket(b) ? k : b), model.activeMonths[0]);
    const out = [{
      icon: CalendarCheck,
      tone: '#2563EB',
      text: (
        <>
          <b>Busiest month:</b> {monthLabel(busiest, true)} with {plural(model.monthTicket(busiest), 'ticket')} solved by{' '}
          {plural(model.monthPeople(busiest), 'person', 'people')}.
        </>
      ),
    }];
    if (!app && data.applications[0]) {
      const a = data.applications[0];
      out.push({
        icon: AppWindow,
        tone: '#7C3AED',
        text: <><b>{a.name}</b> had the most solved tickets: {a.tickets_total} ({pct(a.tickets_total, model.tickets)}% of all).</>,
      });
    }
    if (model.people[0]) {
      const p = model.people[0];
      out.push({
        icon: Trophy,
        tone: '#B45309',
        text: <><b>{p.name}</b> solved the most{app ? <> for {app.name}</> : null}: {plural(p.total, 'ticket')}.</>,
      });
    }
    return out;
  }, [model, app, data]);

  const headerActions = (
    <button type="button" className="tk-btn tk-btn-secondary" onClick={() => navigate('/dashboard')}>
      <ArrowLeft size={16} />
      Overview
    </button>
  );

  if (loading) {
    return (
      <ModuleShell title="Tickets solved 360°" subtitle="Loading…" headerActions={headerActions}>
        <SkeletonBlock rows={10} />
      </ModuleShell>
    );
  }

  return (
    <ModuleShell
      title="Tickets solved 360°"
      subtitle="Complete view of solved tickets — by month, by application and by person."
      headerActions={headerActions}
    >
      <div data-testid="solved-360-page" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {error && <div style={{ padding: 12, background: '#FEE2E2', color: '#B91C1C', borderRadius: 8 }}>{error}</div>}

        {/* Filters */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#6B7280' }}>PERIOD:</span>
            <div role="group" aria-label="Period" style={{ display: 'inline-flex', border: '1px solid #D1D5DB', borderRadius: 6, overflow: 'hidden' }}>
              {PERIODS.map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setParams({ months: n })}
                  style={{
                    padding: '5px 12px', fontSize: 12, border: 'none', cursor: 'pointer',
                    background: months === n ? '#002FA7' : 'white', color: months === n ? 'white' : '#374151',
                    fontWeight: months === n ? 700 : 500,
                  }}
                >
                  Last {n} months
                </button>
              ))}
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, color: '#6B7280' }}>
            APPLICATION:
            <select
              value={app ? appKey : 'all'}
              onChange={(e) => setParams({ app: e.target.value })}
              data-testid="solved-360-app"
              style={{ height: 30, padding: '0 8px', fontSize: 12, border: '1px solid #D1D5DB', borderRadius: 6, background: 'white', minWidth: 200 }}
            >
              <option value="all">All applications</option>
              {(data?.applications || []).map((a) => <option key={a.key} value={a.key}>{a.name}</option>)}
            </select>
          </label>
        </div>

        {!model ? (
          <EmptyState icon={CheckCircle} title="No solved tickets" description={`No tickets were solved in the last ${plural(months, 'month')}.`} />
        ) : (
          <>
            {/* Summary */}
            <div className="sla-pipeline-summary" role="group" aria-label="Solved ticket totals">
              <div className="sla-summary-chip">
                <span className="sla-summary-chip-value">{model.tickets}</span>
                <span className="sla-summary-chip-label">Tickets solved</span>
              </div>
              <div className="sla-summary-chip">
                <span className="sla-summary-chip-value">{model.people.length}</span>
                <span className="sla-summary-chip-label">People</span>
              </div>
              <div className="sla-summary-chip">
                <span className="sla-summary-chip-value">{model.apps.length}</span>
                <span className="sla-summary-chip-label">{model.apps.length === 1 ? 'Application' : 'Applications'}</span>
              </div>
              <div className="sla-summary-chip">
                <span className="sla-summary-chip-value">
                  {model.activeMonths.length ? Math.round(model.tickets / model.activeMonths.length) : 0}
                </span>
                <span className="sla-summary-chip-label">Avg per month</span>
              </div>
              <div className="sla-summary-chip">
                <span className="sla-summary-chip-value">{model.shared}</span>
                <span className="sla-summary-chip-label">Solved by 2+ people</span>
              </div>
            </div>

            <Insights items={insights} />

            {/* Tabs */}
            <div role="tablist" style={{ display: 'flex', gap: 4, borderBottom: '1px solid #E5E7EB', flexWrap: 'wrap' }}>
              {VIEWS.map((v) => {
                const active = v.id === view;
                return (
                  <button
                    key={v.id}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    data-testid={`solved-360-tab-${v.id}`}
                    onClick={() => setParams({ view: v.id })}
                    style={{
                      padding: '8px 14px', fontSize: 13, fontWeight: active ? 700 : 500, cursor: 'pointer',
                      background: 'none', border: 'none', borderBottom: `2px solid ${active ? '#002FA7' : 'transparent'}`,
                      color: active ? '#002FA7' : '#4B5563', marginBottom: -1,
                    }}
                  >
                    {v.label}
                  </button>
                );
              })}
            </div>

            {view === 'month' && (
              <ByMonth data={data} model={model} app={app} onMonth={(k) => setParams({ view: 'person', month: k })} />
            )}
            {view === 'application' && (
              <ByApplication data={data} model={model} onApp={(k) => setParams({ view: 'person', app: k })} />
            )}
            {view === 'person' && (
              <section className="wd-chart-card tk-surface tk-surface-pad is-auto-height" style={{ margin: 0 }}>
                <SectionHead
                  title={pickedMonth === 'all'
                    ? `Who solved the tickets${app ? ` — ${app.name}` : ''}`
                    : `Who solved tickets in ${monthLabel(pickedMonth, true)}${app ? ` — ${app.name}` : ''}`}
                  hint="Click a name to open that person's solved tickets. Shared tickets count for each person."
                />
                <MonthPicker months={model.activeMonths} value={pickedMonth} onChange={(k) => setParams({ month: k })} count={model.monthTicket} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <input
                    type="search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search a person…"
                    aria-label="Search a person"
                    style={{ width: 280, height: 30, padding: '0 8px', fontSize: 12, border: '1px solid #D1D5DB', borderRadius: 6 }}
                  />
                  {query.trim() && (
                    <span style={{ fontSize: 11, color: '#6B7280' }}>
                      {filterPeople(model.people, query).length} of {model.people.length} people match
                    </span>
                  )}
                </div>
                {pickedMonth === 'all' ? (
                  <MonthTable
                    testid="solved-360-person-table"
                    firstHeader="Person"
                    months={data.months}
                    rows={filterPeople(model.people, query)}
                    listAll
                    renderName={(p) => (
                      <button type="button" style={linkBtn} onClick={() => openPerson(p.user_id)} title="Open this person's solved tickets">
                        {p.name}
                      </button>
                    )}
                    getCount={(p, k) => p.counts[k] || 0}
                    extraHeader={app ? null : 'Applications'}
                    renderExtra={app ? null : (p) => p.apps}
                    footer={{ label: 'Tickets solved (each ticket once)', value: model.monthTicket, total: model.tickets }}
                  />
                ) : (
                  <OneMonthTable
                    key={`${pickedMonth}:${appKey}:${query}`}
                    month={pickedMonth}
                    people={filterPeople(model.people, query)}
                    monthTotal={model.monthTicket(pickedMonth)}
                    showApps={!app}
                    onPerson={openPerson}
                    expanded
                  />
                )}
              </section>
            )}
          </>
        )}
      </div>
    </ModuleShell>
  );
}

function SectionHead({ title, hint }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <p className="wd-chart-title">{title}</p>
      {hint && <p className="wd-chart-subtitle">{hint}</p>}
    </div>
  );
}

/** By month: chart with the count on every bar + a month-by-month table. */
function ByMonth({ data, model, app, onMonth }) {
  const rows = data.months.map((k) => ({
    key: k, label: monthLabel(k), full: monthLabel(k, true),
    tickets: model.monthTicket(k), people: model.monthPeople(k),
  }));
  return (
    <>
      <section className="wd-chart-card tk-surface tk-surface-pad is-auto-height" style={{ margin: 0 }}>
        <SectionHead
          title={`Tickets solved per month${app ? ` — ${app.name}` : ''}`}
          hint="The number on each bar is how many tickets were solved that month."
        />
        <div style={{ width: '100%', height: 300 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} margin={{ top: 22, right: 8, left: -10, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke="#EEF0F3" />
              <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#6B7280' }} axisLine={{ stroke: '#E5E7EB' }} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#6B7280' }} axisLine={false} tickLine={false} />
              <Tooltip
                cursor={{ fill: 'rgba(59,130,246,0.06)' }}
                formatter={(v, _n, p) => [`${v} tickets · ${p.payload.people} people`, 'Solved']}
                labelFormatter={(l, p) => p?.[0]?.payload?.full || l}
              />
              <Bar
                dataKey="tickets"
                name="Tickets solved"
                fill="#3B82F6"
                radius={[4, 4, 0, 0]}
                maxBarSize={56}
                cursor="pointer"
                onClick={(entry) => {
                  const k = entry?.payload?.key || entry?.key;
                  if (k && model.monthTicket(k) > 0) onMonth(k);
                }}
              >
                <LabelList dataKey="tickets" position="top" style={{ fontSize: 12, fontWeight: 700, fill: '#111827' }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section className="wd-chart-card tk-surface tk-surface-pad is-auto-height" style={{ margin: 0 }}>
        <SectionHead title="Month by month" hint="Click “See who solved” to list the people for that month." />
        <SyncedHScroll>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: 'white' }} data-testid="solved-360-month-table">
            <thead>
              <tr>
                <th style={{ ...th, textAlign: 'left' }}>Month</th>
                <th style={{ ...th, color: '#111827' }}>Tickets solved</th>
                <th style={th}>People</th>
                {!app && <th style={th}>Applications</th>}
                <th style={th}>Change vs previous month</th>
                <th style={{ ...th, textAlign: 'left' }}>Top solver</th>
                {!app && <th style={{ ...th, textAlign: 'left' }}>Top application</th>}
                <th style={th} />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const prev = i > 0 ? rows[i - 1] : null;
                const t = prev ? trend(r.tickets, prev.tickets) : null;
                const top = r.tickets ? model.topOfMonth(r.key) : null;
                const topApp = r.tickets && !app ? model.topAppOfMonth(r.key) : null;
                const appsActive = data.applications.filter((a) => a.by_month[r.key].tickets > 0).length;
                return (
                  <tr key={r.key} style={{ background: i % 2 ? '#F8FAFC' : 'white' }}>
                    <td style={{ ...td, textAlign: 'left', fontWeight: 600 }}>{r.full}</td>
                    <td style={{ ...td, fontWeight: 700, color: r.tickets ? '#111827' : '#9CA3AF' }}>{r.tickets}</td>
                    <td style={td}>{r.people}</td>
                    {!app && <td style={td}>{appsActive}</td>}
                    <td style={{ ...td, color: t?.tone || '#9CA3AF' }}>{t ? t.word : '—'}</td>
                    <td style={{ ...td, textAlign: 'left' }}>
                      {top && top.counts[r.key] ? <>{top.name} <span style={{ color: '#6B7280' }}>({top.counts[r.key]})</span></> : '—'}
                    </td>
                    {!app && (
                      <td style={{ ...td, textAlign: 'left' }}>
                        {topApp ? <>{topApp.name} <span style={{ color: '#6B7280' }}>({topApp.by_month[r.key].tickets})</span></> : '—'}
                      </td>
                    )}
                    <td style={td}>
                      {r.tickets > 0 && (
                        <button type="button" style={{ ...linkBtn, display: 'inline-flex', alignItems: 'center', gap: 2 }} onClick={() => onMonth(r.key)}>
                          See who solved <CaretRight size={11} weight="bold" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td style={{ ...tf, textAlign: 'left' }}>Total</td>
                <td style={tf}>{model.tickets}</td>
                <td style={tf}>{model.people.length}</td>
                {!app && <td style={tf}>{data.applications.length}</td>}
                <td style={tf} />
                <td style={tf} />
                {!app && <td style={tf} />}
                <td style={tf} />
              </tr>
            </tfoot>
          </table>
        </SyncedHScroll>
      </section>
    </>
  );
}

/** By application: every application × month, people and shared tickets. */
function ByApplication({ data, model, onApp }) {
  const rows = model.apps.map((a) => ({ ...a, key: a.key, total: a.tickets_total }));
  return (
    <section className="wd-chart-card tk-surface tk-surface-pad is-auto-height" style={{ margin: 0 }}>
      <SectionHead
        title="Tickets solved by application"
        hint="Click an application to see who solved its tickets, month by month."
      />
      <MonthTable
        testid="solved-360-application-table"
        firstHeader="Application"
        months={data.months}
        rows={rows}
        listAll
        renderName={(a) => (
          <button type="button" style={linkBtn} onClick={() => onApp(a.key)} title="See who solved this application's tickets">
            {a.name}
          </button>
        )}
        getCount={(a, k) => a.by_month[k].tickets}
        extraHeader="People · solved by 2+"
        renderExtra={(a) => `${a.people_total} · ${a.multi_person_total}`}
        footer={{
          label: 'All applications',
          value: model.monthTicket,
          total: model.tickets,
          extra: `${model.people.length} · ${model.shared}`,
        }}
      />
    </section>
  );
}
